//
// Copyright (c) 2025 Comcast
//

import { useEffect, Suspense, lazy } from 'react'
import { Route, Routes, useLocation, useNavigate, Navigate, useSearchParams, Outlet } from 'react-router'
import { useSelector, useDispatch } from 'react-redux'

import { useOnMount } from 'utils/hooks'

import * as sessionSelector from 'selectors/session'
import * as sessionUserSelector from 'selectors/sessionUser'
import * as ticketingSystemsSelector from 'selectors/ticketingSystems'

import { signOut } from 'reducers/session'
import { restoreSession, authUser } from 'api/sessionUser'

import LoadingIndicator from 'components/Shared/LoadingIndicator/Index'
import LandingPage from 'components/App/LandingPage/Index'
import SignInPanelSandboxPage from './SignInPanelSandbox/Index'
import SignInPanel from './SignInPanel/Index'

import WelcomePage from 'components/App/WelcomePage/Index'
import SelectHomePage from 'components/App/SelectHomePage/Index'
import TicketsPage from 'components/App/TicketsPage/Index'
import TicketCreatePage from 'components/App/TicketCreatePage/Index'
import TicketEditPage from 'components/App/TicketEditPage/Index'
import TicketClonePage from 'components/App/TicketClonePage/Index'
import TicketRequestsPage from 'components/App/TicketRequestsPage/Index'
import TicketPage from 'components/App/TicketPage/Index'
import TicketsSearchResultsPage from 'components/App/TicketsSearchResultsPage/Index'

import TicketProvidersListPage from 'components/App/TicketProvidersListPage/Index'
import TicketProviderTicketsPage from 'components/App/TicketProviderTicketsPage/Index'
import TicketProviderTicketPage from 'components/App/TicketProviderTicketPage/Index'
import TicketProviderTicketsSearchResultsPage from 'components/App/TicketProviderTicketsSearchResultsPage/Index'

import ReleaseNotesPage from 'components/App/ReleaseNotesPage/Index'
import ReleaseNotesAddPage from 'components/App/ReleaseNotesAddPage/Index'
import ReleaseNotesEditPage from 'components/App/ReleaseNotesEditPage/Index'

import PartnerSatClientsPage from 'components/App/PartnerSatClientsPage/Index'
import PartnerSatSecretsPage from 'components/App/PartnerSatSecretsPage/Index'

import ProfilePage from 'components/App/ProfilePage/Index'
import SubscriptionsPage from 'components/App/SubscriptionsPage/Index'

import ErrorPage404 from 'components/App/ErrorPage/ErrorPage404'
import ErrorPage500 from 'components/App/ErrorPage/ErrorPage500'

import DashboardPage from 'components/App/DashboardPage/Index'

import DefaultLayout from 'components/App/Layout/Index'

import GlobalStyle from 'components/utils/globalStyles'

import { AbilityContext } from '../../ability-context'
import buildAbilityFor from '../../abilities'
import homePageTypes from 'constants/homePageTypes'

import {
  RouteTicketingSystems,
  RouteTicketingSystemsTickets,
  RouteTicketingSystemsTicketNew,
  RouteTicketingSystemsTicketEdit,
  RouteTicketingSystemsTicketClone,
  RouteTicketingSystemsTicketAppRequests,
  RouteTicketingSystemsTicket,
  RouteTicketsSearchResults,
  RouteTicketProviders,
  RouteTicketProviderTickets,
  RouteTicketProviderTicket,
  RouteTicketProviderTicketsSearchResults,
  RouteProfile,
  RouteSubscriptionsViews,
  RouteSubscriptionsTickets,
  RouteReleaseNotes,
  RouteReleaseNotesAdd,
  RouteReleaseNotesEdit,
  RouteApiDoc,
  RouteAdminPartners,
  RouteAdminPartnersTicketingSystems,
  RouteAdminPartnersTicketingSystemColumns,
  RouteAdminPartnersOauthAppsPage,
  RouteAdminProviders,
  RouteAdminProviderQueries,
  RouteAdminProviderTicketBoards,
  RouteAdminProviderTicketBoardTicketTableEditPage,
  RouteAdminProviderTicketBoardCsvExportTableEditPage,
  RouteAdminSatClientsPage,
  RouteAdminSatSecretsPage,
  RouteAdminSharedFieldMaps,
  RouteAdminSharedFieldMapColumns,
  RouteAdminStatusCollections,
  RouteAdminStatusCollection,
  RouteAdminStatusCollectionExternalStatuses,
  RouteAdminMimeTypeCollections,
  RouteAdminMimeTypeCollection,
  RouteAdminProviderCredentials,
  RouteAppRequests,
  RouteAdminAuditHistory,
  RouteAdminUsers,
  RouteAdminEmailHistory,
  RouteAdminUserFilters,
  RouteAdminSubscriptionsTickets,
  RouteAdminSubscriptionsViews,
  RouteAdminFeedback,
  RouteError500Show,
  RouteError500Test,
  RouteErrorUnhandledTest,
  RouteSandboxSession,
  RouteSatClientsPage,
  RouteSatSecretsPage,
  RouteAdminWebhookEvents,
  RouteApiDocWithPartnerName,
} from '../../routes'


const EmailsPage = lazy(() => import(/* webpackChunkName: "admin" */'components/App/EmailsPage/Index'))
const UsersPage = lazy(() => import(/* webpackChunkName: "admin" */'components/App/metadataPages/UsersPage/Index'))
const UserFiltersPage = lazy(() => import(/* webpackChunkName: "admin" */'components/App/metadataPages/UserFiltersPage/Index'))
const AdminSubscriptionsTicketsPage = lazy(() => import(/* webpackChunkName: "admin" */'components/App/metadataPages/AdminSubscriptionsPage/Index'))
const AdminSubscriptionsViewsPage = lazy(() => import(/* webpackChunkName: "admin" */'components/App/metadataPages/AdminSubscriptionsPage/Index'))

const PartnersPage = lazy(() => import(/* webpackChunkName: "admin" */'components/App/PartnersPage/Index'))

const PartnerTicketingSystemsPage = lazy(() => import(/* webpackChunkName: "admin" */'components/App/PartnerTicketingSystemsPage/Index'))
const TicketingSystemColumnsPage = lazy(() => import(/* webpackChunkName: "admin" */'components/App/columnsPages/TicketingSystemColumnsPage/Index'))

const PartnerOauthAppsPage = lazy(() => import(/* webpackChunkName: "admin" */'components/App/PartnerOauthAppsPage/Index'))

const SatClientsPage = lazy(() => import(/* webpackChunkName: "admin" */'components/App/SatClientsPage/Index'))
const SatSecretsPage = lazy(() => import(/* webpackChunkName: "admin" */'components/App/SatSecretsPage/Index'))

const SharedFieldsMapsPage = lazy(() => import(/* webpackChunkName: "admin" */'components/App/SharedFieldsMapsPage/Index'))
const SharedColumnsPage = lazy(() => import(/* webpackChunkName: "admin" */'components/App/columnsPages/SharedColumnsPage/Index'))

const TicketProvidersPage = lazy(() => import(/* webpackChunkName: "admin" */'components/App/TicketProvidersPage/Index'))
const TicketProviderQueriesPage = lazy(() => import(/* webpackChunkName: "admin" */'components/App/TicketProviderQueriesPage/Index'))
const TicketProviderTicketBoardsPage = lazy(() => import(/* webpackChunkName: "admin" */'components/App/TicketProviderTicketBoardsPage/Index'))
const TicketProviderTicketBoardTicketTableEditPage = lazy(() => import(/* webpackChunkName: "admin" */'components/App/TicketProviderTicketBoardTicketTableEditPage/Index'))
const TicketProviderTicketBoardCsvExportTableEditPage = lazy(() => import(/* webpackChunkName: "admin" */'components/App/TicketProviderTicketBoardCsvExportTableEditPage/Index'))

const StatusCollectionsPage = lazy(() => import(/* webpackChunkName: "admin" */'components/App/StatusCollectionsPage/Index'))
const StatusCollectionPage = lazy(() => import(/* webpackChunkName: "admin" */'components/App/StatusCollectionPage/Index'))
const StatusCollectionExternalStatusesPage = lazy(() => import(/* webpackChunkName: "admin" */'components/App/StatusCollectionExternalStatusesPage/Index'))

const MimeTypeCollectionsPage = lazy(() => import(/* webpackChunkName: "admin" */'components/App/MimeTypeCollectionsPage/Index'))
const MimeTypeCollectionPage = lazy(() => import(/* webpackChunkName: "admin" */'components/App/MimeTypeCollectionPage/Index'))

const ProviderCredentialsPage = lazy(() => import(/* webpackChunkName: "admin" */'components/App/ProviderCredentialsPage/Index'))

const AppRequestsPage = lazy(() => import(/* webpackChunkName: "admin" */'components/App/AppRequestsPage/Index'))

const AuditsPage = lazy(() => import(/* webpackChunkName: "admin" */'components/App/AuditsPage/Index'))
const FeedbackPage = lazy(() => import(/* webpackChunkName: "admin" */'components/App/FeedbackPage/Index'))

const WebhookEventsPage = lazy(() => import(/* webpackChunkName: "admin" */'components/App/WebhookEventsPage/Index'))

const ApiDocPage = lazy(() => import(/* webpackChunkName: "apidoc" */'components/App/ApiDocPage/Index'))

// eslint-disable-next-line react/prop-types
const NoNavBarLayout = ({ children }) => (
  <DefaultLayout noNavBar>
    {children}
  </DefaultLayout>
)

// eslint-disable-next-line react/prop-types
const ErrorLayout = ({ children }) => (
  <DefaultLayout makeError>
    {children}
  </DefaultLayout>
)

// eslint-disable-next-line react/prop-types
const RouteGuard = ({ shouldRedirect }) => {
  if (shouldRedirect) {
    return <Navigate to='/' replace />
  }
  return <Outlet />
}

const TestError500 = () => {
  throw new Error('Test error 500')
}

const PageLoading = () => (
  <DefaultLayout>
    <GlobalStyle />
    <LoadingIndicator />
  </DefaultLayout>
)

const App = () => {
  const dispatch = useDispatch()
  const authPending = useSelector(sessionSelector.authPending)
  const appLoading = useSelector(sessionSelector.appLoading)
  const authenticated = useSelector(sessionSelector.authenticated)
  const userIsAdmin = useSelector(sessionUserSelector.isAdmin)
  const userIsAdminReadOnly = useSelector(sessionUserSelector.isAdminReadOnly)
  const isUserPrivileged = useSelector(sessionUserSelector.isUserPrivileged)
  const isAnyAdmin = useSelector(sessionUserSelector.isAnyAdmin)
  const isPartnerManager = useSelector(sessionUserSelector.isPartnerManager)
  const userIsPowerUser = useSelector(sessionUserSelector.isPowerUser)
  const isRedirectFromAdminPage = !isAnyAdmin && !userIsPowerUser
  const userRole = useSelector(sessionUserSelector.role)
  const userHomePage = useSelector(sessionUserSelector.homePage)
  const features = useSelector(sessionUserSelector.features)
  const noTicketingSystemsWithApiAccess = useSelector(ticketingSystemsSelector.noTicketingSystemsWithApiAccess)
  const ticketingSystems = useSelector(ticketingSystemsSelector.ticketingSystems) || []
  const noTicketingSystems = ticketingSystems.length === 0
  const location = useLocation()
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()

  useOnMount(() => {
    const token = searchParams.get('code')
    if (token) {
      const partnerId = sessionStorage.getItem('partnerId') || ''
      dispatch(authUser.initiate({ token, partnerId }))
      sessionStorage.removeItem('partnerId')
    } else if (location.pathname.includes('/sessions')) {
      dispatch(signOut({ redirectUrl: '/' }))
    } else if (location.pathname.includes('/sandbox_logout')) {
      dispatch(signOut({ redirectUrl: RouteSandboxSession.pattern }))
    } else {
      dispatch(restoreSession.initiate())
    }
  })

  useEffect(() => {
    const token = searchParams.get('code')
    if (token) {
      const requestedPath = sessionStorage.getItem('requestedPath') || '/'
      navigate(requestedPath)
      sessionStorage.removeItem('requestedPath')
    }
  }, [navigate, searchParams])

  useEffect(() => {
    if (!authenticated) {
      return
    }

    // store variables that are used from components in confirmDialog
    // Context is not available there because separate React tree is used,
    // so useSelector() and useLocation() don't work there
    localStorage.setItem('user_features', features)
    localStorage.setItem('user_is_admin', userIsAdmin)
  }, [authenticated]) // eslint-disable-line react-hooks/exhaustive-deps

  if (authPending || appLoading) {
    return (
      <>
        <GlobalStyle />
        <LoadingIndicator />
      </>
    )
  }

  if (authenticated) {
    const isAnyAdmin = userIsAdmin || userIsAdminReadOnly
    let redirectedSingleTsDisplayId

    let RedirectedPage
    let Layout = DefaultLayout

    if (isUserPrivileged) {
      RedirectedPage = TicketProvidersListPage
    } else {
      if (noTicketingSystems) {
        RedirectedPage = LandingPage
      } else if (noTicketingSystemsWithApiAccess || (userHomePage === homePageTypes.TICKETS)) {
        if (ticketingSystems.length === 1 && location.pathname === '/') {
          redirectedSingleTsDisplayId = ticketingSystems[0].displayId
        } else {
          RedirectedPage = WelcomePage
        }
      } else if (userHomePage === homePageTypes.STATS) {
        RedirectedPage = DashboardPage
      }
    }

    if (!RedirectedPage && !redirectedSingleTsDisplayId) {
      RedirectedPage = SelectHomePage
      Layout = NoNavBarLayout
    }

    const IS_PARTNERS_HAVE_ACCESS_TO_SAT_CLIENTS = features.includes('RELAY_3359_PARTNERS_HAVE_ACCESS_TO_SAT_CLIENTS')

    const RouterRoutes = (
      <Routes>
        <Route element={<Layout />}>
          {redirectedSingleTsDisplayId && <Route path='/' element={<Navigate to={RouteTicketingSystemsTickets({ ticketingSystemDisplayId: redirectedSingleTsDisplayId })} />} />}
          <Route path='/' element={<RedirectedPage />} />

          <Route path='/v2' element={<Navigate to='/' />} />

          {!userHomePage && !noTicketingSystemsWithApiAccess && <Route path='/' />}

          <Route path={RouteTicketingSystems.pattern} element={<WelcomePage />} />
          <Route path={RouteTicketingSystemsTickets.pattern} element={<TicketsPage />} />
          <Route path={RouteTicketingSystemsTicketNew.pattern} element={<TicketCreatePage />} />
          <Route path={RouteTicketingSystemsTicketEdit.pattern} element={<TicketEditPage />} />
          <Route path={RouteTicketingSystemsTicketClone.pattern} element={<TicketClonePage />} />
          <Route path={RouteTicketingSystemsTicketAppRequests.pattern} element={<TicketRequestsPage />} />
          <Route path={RouteTicketingSystemsTicket.pattern} element={<TicketPage />} />
          <Route path={RouteTicketProviders.pattern} element={<TicketProvidersListPage />} />
          <Route path={RouteTicketProviderTickets.pattern} element={<TicketProviderTicketsPage />} />
          <Route path={RouteTicketProviderTicket.pattern} element={<TicketProviderTicketPage />} />
          <Route path={RouteProfile.pattern} element={<ProfilePage />} />
          <Route path={RouteSubscriptionsViews.pattern} element={<SubscriptionsPage />} />
          <Route path={RouteSubscriptionsTickets.pattern} element={<SubscriptionsPage />} />
          <Route path={RouteTicketsSearchResults.pattern} element={<TicketsSearchResultsPage />} />
          <Route path={RouteTicketProviderTicketsSearchResults.pattern} element={<TicketProviderTicketsSearchResultsPage />} />

          {Boolean(process.env.SAT_API_ENABLED === 'true') && isPartnerManager && !isAnyAdmin && IS_PARTNERS_HAVE_ACCESS_TO_SAT_CLIENTS && (
            <>
              <Route path={RouteSatClientsPage.pattern} element={<PartnerSatClientsPage />} />
              <Route path={RouteSatSecretsPage.pattern} element={<PartnerSatSecretsPage />} />
            </>
          )}

          <Route element={<RouteGuard shouldRedirect={isRedirectFromAdminPage} />}>
            <Route path={RouteAppRequests.pattern} element={<AppRequestsPage />} />
            <Route path={RouteAdminUsers.pattern} element={<UsersPage />} />
            <Route path={RouteAdminEmailHistory.pattern} element={<EmailsPage />} />
            <Route path={RouteAdminUserFilters.pattern} element={<UserFiltersPage />} />
            <Route path={RouteAdminSubscriptionsTickets.pattern} element={<AdminSubscriptionsTicketsPage />} />
            <Route path={RouteAdminSubscriptionsViews.pattern} element={<AdminSubscriptionsViewsPage />} />
            <Route path={RouteAdminFeedback.pattern} element={<FeedbackPage />} />

            <Route path={RouteAdminPartners.pattern} element={<PartnersPage />} />
            <Route path={RouteAdminPartnersTicketingSystems.pattern} element={<PartnerTicketingSystemsPage />} />
            <Route path={RouteAdminPartnersTicketingSystemColumns.pattern} element={<TicketingSystemColumnsPage />} />

            <Route path={RouteAdminPartnersOauthAppsPage.pattern} element={<PartnerOauthAppsPage />} />

            {Boolean(process.env.SAT_API_ENABLED === 'true') && (
              <>
                <Route path={RouteAdminSatClientsPage.pattern} element={<SatClientsPage />} />
                <Route path={RouteAdminSatSecretsPage.pattern} element={<SatSecretsPage />} />
              </>
            )}

            <Route path={RouteAdminSharedFieldMaps.pattern} element={<SharedFieldsMapsPage />} />
            <Route path={RouteAdminSharedFieldMapColumns.pattern} element={<SharedColumnsPage />} />

            <Route path={RouteAdminProviders.pattern} element={<TicketProvidersPage />} />
            <Route path={RouteAdminProviderQueries.pattern} element={<TicketProviderQueriesPage />} />
            <Route path={RouteAdminProviderTicketBoards.pattern} element={<TicketProviderTicketBoardsPage />} />
            <Route path={RouteAdminProviderTicketBoardTicketTableEditPage.pattern} element={<TicketProviderTicketBoardTicketTableEditPage />} />
            <Route path={RouteAdminProviderTicketBoardCsvExportTableEditPage.pattern} element={<TicketProviderTicketBoardCsvExportTableEditPage />} />
            <Route path={RouteAdminStatusCollections.pattern} element={<StatusCollectionsPage />} />
            <Route path={RouteAdminStatusCollection.pattern} element={<StatusCollectionPage />} />
            <Route path={RouteAdminStatusCollectionExternalStatuses.pattern} element={<StatusCollectionExternalStatusesPage />} />
            <Route path={RouteAdminMimeTypeCollections.pattern} element={<MimeTypeCollectionsPage />} />
            <Route path={RouteAdminMimeTypeCollection.pattern} element={<MimeTypeCollectionPage />} />

            <Route path={RouteAdminProviderCredentials.pattern} element={<ProviderCredentialsPage />} />

            <Route path={RouteAdminAuditHistory.pattern} element={<AuditsPage />} />

            {Boolean(process.env.ENABLE_TS_WEBHOOKS === 'true') && (
              <Route path={RouteAdminWebhookEvents.pattern} element={<WebhookEventsPage />} />
            )}
          </Route>

          <Route element={<RouteGuard shouldRedirect={noTicketingSystems} />}>
            <Route path={RouteReleaseNotes.pattern} element={<ReleaseNotesPage />} />
            <Route path={RouteReleaseNotesAdd.pattern} element={<ReleaseNotesAddPage />} />
            <Route path={RouteReleaseNotesEdit.pattern} element={<ReleaseNotesEditPage />} />

            <Route path={RouteApiDoc.pattern} element={<ApiDocPage />} />
            <Route path={RouteApiDocWithPartnerName.pattern} element={<ApiDocPage />} />
          </Route>

          <Route path={RouteError500Show.pattern} element={<ErrorPage500 />} />
          <Route path={RouteError500Test.pattern} element={<TestError500 />} />
        </Route>

        <Route element={<ErrorLayout />}>
          <Route path={RouteErrorUnhandledTest.pattern} element={<ErrorPage500 />} />
        </Route>

        <Route element={<NoNavBarLayout />}>
          <Route path='*' element={<ErrorPage404 />} />
        </Route>
      </Routes>
    )

    return (
      <AbilityContext.Provider value={buildAbilityFor(userRole)}>
        <Suspense fallback={<PageLoading />}>
          {RouterRoutes}
        </Suspense>
      </AbilityContext.Provider>
    )
  } else {
    const requestedPath = location.pathname + location.search + location.hash
    sessionStorage.setItem('requestedPath', requestedPath)
    return (
      <Routes>
        <Route element={<NoNavBarLayout />}>
          <Route path={RouteSandboxSession.pattern} element={<SignInPanelSandboxPage />} />
          <Route path='*' element={<SignInPanel />} />
        </Route>
      </Routes>
    )
  }
}

export default App
